import { useEffect, useMemo, useState } from 'react';
import { api, fmtCRC, fmtDate } from '../api';
import Modal from '../components/Modal';
import SearchSelect from '../components/SearchSelect';
import { IconBank, IconPlus, IconX } from '../components/Icons';
import PlatformVisual from '../components/PlatformVisual';

const empty = { kind: 'cuenta', platformId: '', email: '', password: '', totalProfiles: 4, renewalDay: '', cost: '', notes: '' };

// Espacios de la cuenta: los ocupados primero y el resto libres. Una venta de
// cuenta completa toma todo, así que se muestra como un único espacio.
function profileSlots(account, profiles = []) {
  const full = profiles.find((p) => p.unit === 'cuenta');
  if (full) return [{ ...full, full: true }];
  const libres = Math.max(account.totalProfiles - profiles.length, 0);
  return [...profiles, ...Array.from({ length: libres }, () => ({}))];
}

export default function Accounts() {
  const [accounts, setAccounts] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [q, setQ] = useState('');
  const [accountFilter, setAccountFilter] = useState('');
  const [modal, setModal] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(empty);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState('');
  const [notice, setNotice] = useState({ id: '', text: '' });

  const load = () => api('/accounts').then(setAccounts).catch(() => {});
  useEffect(() => {
    load();
    api('/platforms').then(setPlatforms).catch(() => {});
  }, []);

  const open = (a) => {
    setEdit(a || null);
    setForm(
      a
        ? {
            kind: a.kind || 'cuenta',
            platformId: a.platformId?._id || '',
            email: a.email || '',
            password: a.password || '',
            totalProfiles: a.totalProfiles,
            renewalDay: a.renewalDay || '',
            cost: a.cost || '',
            notes: a.notes || '',
          }
        : empty
    );
    setError('');
    setModal(true);
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    try {
      const body = {
        ...form,
        totalProfiles: Number(form.totalProfiles),
        cost: Number(form.cost || 0),
        renewalDay: form.renewalDay ? Number(form.renewalDay) : undefined,
      };
      if (edit) await api(`/accounts/${edit._id}`, { method: 'PUT', body });
      else await api('/accounts', { method: 'POST', body });
      setModal(false);
      setForm(empty);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  // Al desplegar se consulta quién ocupa cada perfil de la cuenta
  const toggle = async (a) => {
    if (expanded === a._id) {
      setExpanded(null);
      setDetail(null);
      return;
    }
    setExpanded(a._id);
    setDetail(null);
    try {
      setDetail(await api(`/accounts/${a._id}/profiles`));
    } catch (err) {
      setNotice({ id: a._id, text: err.message });
    }
  };

  const release = async (a, profile) => {
    const nombre = profile.client?.name || profile.profileName || 'este perfil';
    if (!confirm(`¿Liberar el perfil de ${nombre}? La suscripción se conserva, pero deja de ocupar un espacio de esta cuenta.`)) return;
    setBusy(profile.subscriptionId);
    try {
      await api(`/accounts/${a._id}/profiles/${profile.subscriptionId}`, { method: 'DELETE' });
      setNotice({ id: '', text: '' }); // el aviso de "no se puede eliminar" ya no aplica
      setDetail(await api(`/accounts/${a._id}/profiles`));
      load();
    } catch (err) {
      setNotice({ id: a._id, text: err.message });
    } finally {
      setBusy('');
    }
  };

  const remove = async (a) => {
    if (!confirm(`¿Eliminar ${a.email || 'esta entrada'}?`)) return;
    try {
      await api(`/accounts/${a._id}`, { method: 'DELETE' });
      setNotice({ id: '', text: '' });
      load();
    } catch (err) {
      // Si está ocupada, se abre la cuenta para poder liberar los perfiles ahí mismo
      setNotice({ id: a._id, text: err.message });
      if (expanded !== a._id) toggle(a);
    }
  };

  const s = q.trim().toLowerCase();
  const shown = useMemo(
    () => accounts.filter((a) => {
      if (accountFilter && a._id !== accountFilter) return false;
      return (a.email || '').toLowerCase().includes(s) || (a.platformId?.name || '').toLowerCase().includes(s);
    }),
    [accounts, accountFilter, s]
  );

  const totalProfiles = accounts.reduce((sum, a) => sum + a.totalProfiles, 0);
  const usedProfiles = accounts.reduce((sum, a) => sum + a.usedProfiles, 0);
  const isProfiles = form.kind === 'perfiles';

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <IconBank size={24} />
          <h1 className="text-2xl font-bold">Banco de Cuentas</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <select
            className="input min-w-[210px] flex-1 sm:flex-none"
            value={accountFilter}
            onChange={(e) => setAccountFilter(e.target.value)}
            aria-label="Filtrar por cuenta"
          >
            <option value="">Todas las cuentas</option>
            {accounts.map((a) => (
              <option key={a._id} value={a._id}>
                {a.platformId?.name || 'Sin plataforma'} · {a.email || (a.kind === 'perfiles' ? 'Perfiles sueltos' : 'Sin correo')}
              </option>
            ))}
          </select>
          <input className="input max-w-xs" placeholder="Buscar cuenta…" value={q} onChange={(e) => setQ(e.target.value)} />
          <button className="btn-primary whitespace-nowrap" onClick={() => open()} disabled={platforms.length === 0}>
            <IconPlus size={16} /> Nueva
          </button>
        </div>
      </div>

      {platforms.length === 0 && (
        <p className="text-slate-500">Primero agrega plataformas en el Catálogo para registrar cuentas.</p>
      )}

      {accounts.length > 0 && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <div className="card">
            <div className="text-sm text-slate-400">Entradas</div>
            <div className="mt-1 text-2xl font-bold">{accounts.length}</div>
          </div>
          <div className="card">
            <div className="text-sm text-slate-400">Perfiles totales</div>
            <div className="mt-1 text-2xl font-bold">{totalProfiles}</div>
          </div>
          <div className="card">
            <div className="text-sm text-slate-400">Ocupados</div>
            <div className="mt-1 text-2xl font-bold text-amber-400">{usedProfiles}</div>
          </div>
          <div className="card">
            <div className="text-sm text-slate-400">Disponibles</div>
            <div className="mt-1 text-2xl font-bold text-emerald-400">{totalProfiles - usedProfiles}</div>
          </div>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {shown.map((a) => {
          const pct = a.totalProfiles ? (a.usedProfiles / a.totalProfiles) * 100 : 0;
          const loose = a.kind === 'perfiles';
          const isOpen = expanded === a._id;
          return (
            <div key={a._id} className="card space-y-3">
              <div className="flex items-start gap-3">
                <button
                  type="button"
                  className="flex min-w-0 flex-1 items-start gap-3 text-left"
                  onClick={() => toggle(a)}
                  title={isOpen ? 'Ocultar perfiles' : 'Ver quién ocupa cada perfil'}
                >
                  <PlatformVisual platform={a.platformId} size="md" className="mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-medium">{a.platformId?.name || '—'}</span>
                      <span className={`badge ${loose ? 'bg-amber-500/20 text-amber-300' : 'bg-slate-700 text-slate-300'}`}>
                        {loose ? 'perfiles' : 'cuenta'}
                      </span>
                      <span className="text-slate-500">{isOpen ? '▾' : '▸'}</span>
                    </div>
                    <div className="truncate text-sm text-slate-400">{a.email || (loose ? 'Perfiles sueltos' : '—')}</div>
                  </div>
                </button>
                <button className="text-xs text-slate-400 hover:text-indigo-300" onClick={() => open(a)}>Editar</button>
                <button className="text-slate-400 hover:text-red-400" title="Eliminar" onClick={() => remove(a)}><IconX size={16} /></button>
              </div>

              <div>
                <div className="mb-1 flex justify-between text-xs text-slate-400">
                  <span>Perfiles</span>
                  <span>
                    <span className="text-slate-200">{a.usedProfiles}</span>/{a.totalProfiles} ·{' '}
                    <span className="text-emerald-400">{a.availableProfiles} libres</span>
                  </span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-slate-800">
                  <div className={`h-full rounded-full transition-all duration-300 ${a.fullTaken ? 'bg-red-500' : 'bg-indigo-500'}`} style={{ width: `${pct}%` }} />
                </div>
              </div>

              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                {a.cost > 0 && <span>Costo: {fmtCRC(a.cost)}/mes</span>}
                {a.renewalDay && <span>Renueva día {a.renewalDay}</span>}
              </div>
              {a.notes && <p className="text-xs text-slate-500">{a.notes}</p>}

              {notice.id === a._id && notice.text && <p className="text-xs text-red-400">{notice.text}</p>}

              {isOpen && (
                <div className="space-y-1.5 border-t border-slate-800 pt-3">
                  {!detail ? (
                    <p className="text-xs text-slate-500">Cargando perfiles…</p>
                  ) : (
                    profileSlots(a, detail.profiles).map((slot, i) => (
                      <div key={slot.subscriptionId || `libre-${i}`} className="flex items-center gap-2 rounded-lg border border-slate-800 bg-slate-900/60 px-2.5 py-2">
                        <span className="w-16 shrink-0 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          {slot.full ? 'Completa' : `Perfil ${i + 1}`}
                        </span>
                        {slot.subscriptionId ? (
                          <>
                            <div className="min-w-0 flex-1">
                              <div className="text-sm">{slot.client?.name || slot.profileName || 'Sin cliente'}</div>
                              <div className="text-[11px] leading-snug text-slate-500">
                                {slot.profileName && slot.client?.name && `${slot.profileName} · `}
                                {slot.nextDueDate ? `vence ${fmtDate(slot.nextDueDate)}` : 'sin fecha'}
                                {slot.pin && ` · PIN ${slot.pin}`}
                              </div>
                            </div>
                            <button
                              type="button"
                              className="shrink-0 rounded-md border border-slate-700 px-2 py-1 text-[11px] font-medium text-slate-300 hover:border-red-500/40 hover:text-red-300 disabled:opacity-50"
                              onClick={() => release(a, slot)}
                              disabled={busy === slot.subscriptionId}
                            >
                              {busy === slot.subscriptionId ? 'Liberando…' : 'Liberar'}
                            </button>
                          </>
                        ) : (
                          <span className="flex-1 text-xs text-emerald-400">Libre</span>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          );
        })}
        {shown.length === 0 && platforms.length > 0 && (
          <p className="text-slate-500">Sin resultados. Agrega una cuenta o unos perfiles sueltos con “Nueva”.</p>
        )}
      </div>

      <Modal open={modal} title={edit ? 'Editar entrada' : 'Nueva entrada'} onClose={() => setModal(false)}>
        <form onSubmit={save} className="space-y-3">
          <div className="flex gap-2">
            <button type="button" onClick={() => setForm({ ...form, kind: 'cuenta' })} className={form.kind === 'cuenta' ? 'btn-primary flex-1' : 'btn-ghost flex-1'}>Cuenta completa</button>
            <button type="button" onClick={() => setForm({ ...form, kind: 'perfiles' })} className={form.kind === 'perfiles' ? 'btn-primary flex-1' : 'btn-ghost flex-1'}>Solo perfiles</button>
          </div>

          <SearchSelect
            value={form.platformId}
            onChange={(v) => setForm({ ...form, platformId: v })}
            required
            placeholder="Plataforma…"
            options={platforms.map((p) => ({ value: p._id, label: p.name }))}
          />

          {!isProfiles && (
            <>
              <input className="input" placeholder="Email de la cuenta" value={form.email} onChange={set('email')} required />
              <input className="input" placeholder="Contraseña de la cuenta" value={form.password} onChange={set('password')} />
            </>
          )}
          {isProfiles && (
            <input className="input" placeholder="Referencia/origen (opcional)" value={form.email} onChange={set('email')} />
          )}

          <div className="flex gap-2">
            <label className="flex-1 text-xs text-slate-400">
              {isProfiles ? 'Cantidad de perfiles' : 'Perfiles (ej. Netflix: 4)'}
              <input className="input mt-1" type="number" min="1" value={form.totalProfiles} onChange={set('totalProfiles')} required />
            </label>
            {!isProfiles && (
              <label className="flex-1 text-xs text-slate-400">
                Día de renovación
                <input className="input mt-1" type="number" min="1" max="31" placeholder="Opcional" value={form.renewalDay} onChange={set('renewalDay')} />
              </label>
            )}
          </div>
          <input className="input" type="number" min="0" placeholder="Costo mensual (₡, opcional)" value={form.cost} onChange={set('cost')} />
          <textarea className="input" rows="2" placeholder="Notas (opcional)" value={form.notes} onChange={set('notes')} />
          {error && <p className="text-sm text-red-400">{error}</p>}
          <button className="btn-primary w-full">{edit ? 'Guardar' : 'Crear'}</button>
        </form>
      </Modal>
    </div>
  );
}
